import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import browserHistory, { registerNavigationBlocker } from '~/browserHistory';

interface IUseNavigationDetectorOptions {
  resetKey?: unknown;
  when?: boolean;
  onNavigationAttempt?: (targetLocation: any, action: string) => void;
  onBrowserUnload?: () => void;
}

export const useNavigationDetector = ({
  resetKey,
  when = false,
  onNavigationAttempt,
  onBrowserUnload,
}: IUseNavigationDetectorOptions = {}) => {
  const location = useLocation();
  const unblockRef = useRef<any>(null);
  const isBlockingRef = useRef(false);
  const pendingNavigationRef = useRef<{ location: any; action: string } | null>(null);

  // Callers commonly pass inline arrows for these. Holding them in refs keeps
  // the block-installing effect from re-running on every render — which would
  // otherwise tear down and reinstall the block right after allowNavigation()
  // cleared it, re-blocking the very navigation the user just confirmed.
  const onNavigationAttemptRef = useRef(onNavigationAttempt);
  const onBrowserUnloadRef = useRef(onBrowserUnload);
  onNavigationAttemptRef.current = onNavigationAttempt;
  onBrowserUnloadRef.current = onBrowserUnload;

  // Helper to get a unique key for a location (pathname + search + hash)
  const getLocationKey = (loc: any) =>
    (loc?.pathname || '') + (loc?.search || '') + (loc?.hash || '');

  const allowNavigation = useCallback(() => {
    isBlockingRef.current = true;
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
  }, []);

  const navigateToPending = useCallback(() => {
    const pending = pendingNavigationRef.current;

    if (pending) {
      const { location: targetLocation, action } = pending;

      // Clear the pending navigation first
      pendingNavigationRef.current = null;

      // Allow navigation by setting blocking to true and removing the block
      isBlockingRef.current = true;

      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }

      // Use setTimeout to ensure the navigation happens after the current execution
      setTimeout(() => {
        if (action === 'REPLACE') {
          browserHistory.replace(
            targetLocation.pathname + targetLocation.search + targetLocation.hash,
            targetLocation.state,
          );
        } else if (action === 'POP') {
          browserHistory.back();
        } else {
          browserHistory.push(
            targetLocation.pathname + targetLocation.search + targetLocation.hash,
            targetLocation.state,
          );
        }
      }, 0);
    } else {
      console.warn(
        'No pending navigation found. Make sure to call this only after onNavigationAttempt.',
      );
    }
  }, []);

  const clearPendingNavigation = useCallback(() => {
    pendingNavigationRef.current = null;
  }, []);

  const blockNavigation = useCallback(() => {
    isBlockingRef.current = false;
  }, []);

  useEffect(() => {
    if (!when) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      onBrowserUnloadRef.current?.();
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    // Handle browser navigation (back/forward buttons, refresh, close tab)
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Handle programmatic navigation within the app
    const unblock = browserHistory.block((tx: any) => {
      // If the navigation is to the same location, allow it
      if (getLocationKey(tx.location) === getLocationKey(location)) {
        return false; // Allow navigation
      }

      if (isBlockingRef.current) {
        // Navigation is explicitly allowed
        return false; // Allow navigation
      }

      // Store the pending navigation
      pendingNavigationRef.current = { location: tx.location, action: tx.action };

      // Always call onNavigationAttempt for each attempt
      onNavigationAttemptRef.current?.(tx.location, tx.action);
      return true; // Block navigation and wait for user decision
    });

    unblockRef.current = unblock;

    // Register this blocker so it can be disabled during force navigation
    const unregisterFromHistory = registerNavigationBlocker(unblock);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      unregisterFromHistory();
    };
    // Depend on the location's primitive fields rather than the object itself —
    // some routers return a fresh location reference on every render, which
    // would tear down and reinstall the block needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when, location.pathname, location.search, location.hash]);

  // Reset to blocking state when location changes or resetKey changes
  useEffect(() => {
    isBlockingRef.current = false;
    pendingNavigationRef.current = null; // Also clear pending navigation on location change
  }, [location.pathname, location.search, location.hash, resetKey]);

  return {
    allowNavigation,
    blockNavigation,
    navigateToPending,
    clearPendingNavigation,
  };
};
